import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {
  mediaLargeScreens,
  mediaTabletAndUp,
} from '../../../constants/customMedia';
import {Button} from '../../atoms/Buttons/Button';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';

const MOBILE_PADDING = 16;
const TABLET_PADDING = 52;
const LARGE_PADDING = 60;

interface ControlButtonElevationCssProps {
  $buttonElevationOverride?: number;
}

interface SlideControlsProps {
  hasOverflow: boolean;
  hideSideButtons: boolean;
  isPrevActive: boolean;
  isNextActive: boolean;
  theme: 'light' | 'dark';
  moveSideNavTop?: boolean;
  navButtonsZIndex?: number;
  loop?: boolean;
  onPrev: () => void;
  onNext: () => void;
  scrollToSlide: (
    index: number,
    options?: {behavior?: 'auto' | 'smooth'},
  ) => void;
  disableTabIndex?: boolean;
}

const THEME: Record<
  'light' | 'dark',
  {
    iconDropShadow: boolean;
    enableButtonShadow: boolean;
    buttonColor?: string;
    enableButtonHover: boolean;
    arrowSize: keyof typeof IconSize;
  }
> = {
  light: {
    iconDropShadow: false,
    enableButtonShadow: true,
    buttonColor: COLORS.White,
    enableButtonHover: false,
    arrowSize: 'XS',
  },
  dark: {
    iconDropShadow: true,
    enableButtonShadow: false,
    enableButtonHover: true,
    arrowSize: 'S' as keyof typeof IconSize,
  },
};

export const SlideControls: React.FC<SlideControlsProps> = ({
  hasOverflow,
  hideSideButtons,
  isPrevActive,
  isNextActive,
  theme,
  moveSideNavTop,
  navButtonsZIndex,
  loop = false,
  onPrev,
  onNext,
  scrollToSlide,
  disableTabIndex = false,
}) => {
  const {t} = useTranslation();

  if (!hasOverflow || hideSideButtons) {
    return null;
  }

  const handleNextClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (isNextActive) {
      onNext();
    } else if (!isNextActive && loop) {
      scrollToSlide(0, {behavior: 'auto'});
    }
  };

  const handlePrevClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (isPrevActive) {
      onPrev();
    }
  };

  return (
    <>
      <ControlButtonWrapperStart
        disabled={!isPrevActive}
        $enableButtonShadow={THEME[theme].enableButtonShadow}
        $enableButtonHover={THEME[theme].enableButtonHover}
        $buttonColor={THEME[theme].buttonColor}
        $moveSideNavTop={moveSideNavTop}
        $buttonElevationOverride={navButtonsZIndex}
        tabIndex={disableTabIndex ? -1 : undefined}
      >
        <ControlButton
          aria-label={t('Go to the previous slide')}
          data-hidden={!isPrevActive}
          data-testid="arrowLeft"
          disableTabNavigation={disableTabIndex}
          onClick={handlePrevClick}
          round
          size="sm"
          tabIndex={disableTabIndex ? -1 : undefined}
          variant="floating"
        >
          <StyledIcon
            size={THEME[theme].arrowSize}
            asset={GenericIconName.ArrowLeft}
            $dropShadow={THEME[theme].iconDropShadow}
          />
        </ControlButton>
      </ControlButtonWrapperStart>
      <ControlButtonWrapperEnd
        disabled={!isNextActive && !loop}
        $enableButtonShadow={THEME[theme].enableButtonShadow}
        $enableButtonHover={THEME[theme].enableButtonHover}
        $buttonColor={THEME[theme].buttonColor}
        $moveSideNavTop={moveSideNavTop}
        $buttonElevationOverride={navButtonsZIndex}
        tabIndex={disableTabIndex ? -1 : undefined}
      >
        <ControlButton
          aria-label={t('Go to the next slide')}
          data-hidden={!isNextActive && !loop}
          data-testid="arrowRight"
          disableTabNavigation={disableTabIndex}
          onClick={handleNextClick}
          round
          size="sm"
          tabIndex={disableTabIndex ? -1 : undefined}
          variant="floating"
        >
          <Icon
            size={THEME[theme].arrowSize}
            asset={GenericIconName.ArrowRight}
          />
        </ControlButton>
      </ControlButtonWrapperEnd>
    </>
  );
};

const ControlButton = styled(Button)`
  && {
    pointer-events: auto;
    min-width: 48px;
  }

  &[data-hidden='true'] {
    display: none;
  }
`;

const ControlButtonWrapperStart = styled.div<
  {
    disabled: boolean;
    $enableButtonShadow: boolean;
    $enableButtonHover: boolean;
    $buttonColor?: string;
    $moveSideNavTop?: boolean;
  } & ControlButtonElevationCssProps
>`
  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute !important;
  pointer-events: none;
  left: 0;

  top: 0;
  bottom: 0;
  padding-bottom: 0;
  width: ${MOBILE_PADDING}px;

  @media ${mediaTabletAndUp} {
    width: ${TABLET_PADDING}px;
  }

  @media ${mediaLargeScreens} {
    width: ${LARGE_PADDING}px;
  }

  ${ControlButton} {
    top: ${props => (props.$moveSideNavTop ? '-50px' : 0)};
  }

  &:hover {
    ${ControlButton} {
      background: ${props => {
        if (props.$enableButtonHover) {
          return 'rgba(0, 0, 0, 0.15)';
        }

        return props.$buttonColor ?? '';
      }};
    }
  }

  z-index: ${props => props.$buttonElevationOverride ?? ''};
`;

const ControlButtonWrapperEnd = styled(ControlButtonWrapperStart)<
  {$moveSideNavTop?: boolean} & ControlButtonElevationCssProps
>`
  left: auto;
  right: 0;

  ${ControlButton} {
    top: ${props => (props.$moveSideNavTop ? '-50px' : 0)};
  }
`;

const StyledIcon = styled(Icon)<{
  $dropShadow: boolean;
}>`
  filter: ${({$dropShadow}) =>
    $dropShadow ? 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.6))' : ''};
`;
